/**
 * SubTasksSection Component
 *
 * Displays related cards (parent and subcards) for a given card,
 * and allows breaking the card down into subcards inline.
 */

import { useState } from 'react';
import { toSafeISOString } from '@/utils/date';
import { BoardTaskPriority } from '@/types/enums/BoardTaskPriority';
import { TaskRelationRelationType } from '@/types/enums/TaskRelationRelationType';
import { BoardTask } from '@/types/models/BoardTask';
import { BoardStatusWithCards } from '@/types/board-config.types';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import { sizing } from '@/styles/design-tokens';
import { Icon, IconName } from '@/icons';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: batch subcard/relation creation uses { toast: false }; per-card toasts in a loop would be spammy
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { copyEntityLink } from '@/utils/clipboard';
import { useToast } from '@/hooks/useToast';
import { logger } from '@/utils/logger';
import { useTaskRelationsQuery } from '@/hooks/queries';
import type {
  GetTaskRelationsAsSourceItem,
  GetTaskRelationsAsTargetItem,
  CreateTaskRelationInput,
  CreateTaskRelationOutput,
  CreateTaskInput,
  CreateTaskOutput,
} from '@/types/generated';

interface SubTasksSectionProps {
  taskId: string;
  cardTitle: string;
  cardPriority?: string;
  statuses: BoardStatusWithCards[];
  boardId: string;
  onSubcardsCreated: (newCards: BoardTask[]) => void;
  /** Whether this card is frozen (past week, non-admin) */
  isFrozen?: boolean;
  /** True only when frozen because the card belongs to a past week (enables carry-over) */
  isFrozenFromPastWeek?: boolean;
  /** Called when a related card should be opened inline */
  onOpenCard?: (taskId: string) => void;
}

const RELATION_TYPE_SUB_CARD = TaskRelationRelationType.SUB_TASK;
const RELATION_TYPE_CARRY_OVER = TaskRelationRelationType.CARRY_OVER;
const RELATION_TYPE_COHORT = TaskRelationRelationType.COHORT;

export default function SubTasksSection({
  taskId,
  cardTitle,
  cardPriority,
  statuses,
  boardId,
  onSubcardsCreated,
  isFrozen = false,
  isFrozenFromPastWeek = false,
  onOpenCard,
}: SubTasksSectionProps) {
  const colors = useThemeColors();
  const toast = useToast();

  const handleCopyLink = async (
    e: React.MouseEvent,
    entity: DeepLinkEntity,
    id: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await copyEntityLink(entity, id);
    if (success) {
      toast.success('Link copied!', {
        message: 'Link has been copied to your clipboard',
        duration: 2000,
      });
    } else {
      toast.error('Failed to copy link', {
        message: 'Please try again or copy from the URL bar',
        duration: 3000,
      });
    }
  };

  const { data: relationsData, isLoading: isLoadingRelations } =
    useTaskRelationsQuery(taskId);

  const subcards: GetTaskRelationsAsSourceItem[] =
    relationsData?.as_source.filter(
      r => r.relation_type === RELATION_TYPE_SUB_CARD
    ) ?? [];
  const parentCards: GetTaskRelationsAsTargetItem[] =
    relationsData?.as_target.filter(
      r => r.relation_type === RELATION_TYPE_SUB_CARD
    ) ?? [];

  const cohortParent: GetTaskRelationsAsTargetItem | undefined =
    relationsData?.as_target.find(
      r => r.relation_type === RELATION_TYPE_COHORT
    );

  const carryOverTarget = relationsData?.as_source.find(
    r => r.relation_type === RELATION_TYPE_CARRY_OVER
  );
  const carriedFromSource = relationsData?.as_target.find(
    r => r.relation_type === RELATION_TYPE_CARRY_OVER
  );

  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [isCarryingOver, setIsCarryingOver] = useState(false);
  const [headings, setHeadings] = useState<string[]>(['']);
  const [isCreatingSubcards, setIsCreatingSubcards] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);

  const handleAddHeading = () => {
    setHeadings(prev => [...prev, '']);
  };

  const handleHeadingChange = (index: number, value: string) => {
    setHeadings(prev => prev.map((h, i) => (i === index ? value : h)));
  };

  const handleRemoveHeading = (index: number) => {
    if (headings.length <= 1) return;
    setHeadings(prev => prev.filter((_, i) => i !== index));
  };

  const handleHeadingKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (index === headings.length - 1) {
        handleAddHeading();
      }
    }
  };

  const handleCreateSubcards = async () => {
    const validHeadings = headings.filter(h => h.trim() !== '');
    if (validHeadings.length === 0) return;

    // Guard: need at least one status column
    if (statuses.length === 0) {
      setCreationError(
        'This board has no columns. Add a column before creating subcards.'
      );
      return;
    }

    // Get leftmost status for new cards
    const firstStatus = statuses.reduce<(typeof statuses)[0]>(
      (min, s) => (s.position < min.position ? s : min),
      statuses[0]
    );

    setIsCreatingSubcards(true);
    setCreationError(null);

    const errors: string[] = [];
    const createdCards: BoardTask[] = [];
    const priority = (cardPriority ||
      BoardTaskPriority.MEDIUM) as BoardTaskPriority;

    for (const heading of validHeadings) {
      const title = `${cardTitle} - ${heading}`;
      try {
        // Step 1: Create the subcard
        const createResult = await actionOrchestrator.execute<
          CreateTaskInput,
          CreateTaskOutput
        >(
          Action.CREATE_TASK,
          {
            board_id: boardId,
            status_id: firstStatus.id!,
            title,
            priority,
          },
          { toast: false }
        );

        if (
          createResult.code !== ResponseCode.SUCCESS ||
          !createResult.data?.task_id
        ) {
          errors.push(`Failed to create subcard: "${heading}"`);
          continue;
        }

        // Step 2: Create the relation
        const relationResult = await actionOrchestrator.execute<
          CreateTaskRelationInput,
          CreateTaskRelationOutput
        >(
          Action.CREATE_TASK_RELATION,
          {
            source_task_id: taskId,
            target_task_id: createResult.data.task_id,
            relation_type: RELATION_TYPE_SUB_CARD,
          },
          { toast: false }
        );

        if (relationResult.code !== ResponseCode.SUCCESS) {
          errors.push(`Failed to link subcard: "${heading}"`);
        } else {
          createdCards.push({
            id: createResult.data.task_id,
            board_id: boardId,
            organization_id: '',
            status_id: firstStatus.id!,
            title,
            priority,
            created_by: '',
            created_at: toSafeISOString(new Date()),
          });
        }
      } catch (error) {
        logger.error('Error creating subcard:', error);
        errors.push(`Error creating subcard: "${heading}"`);
      }
    }

    setIsCreatingSubcards(false);

    if (errors.length > 0) {
      setCreationError(errors.join('; '));
      // Keep panel open — user sees errors, can retry
    } else {
      setIsBreakdownOpen(false);
      setHeadings(['']);
    }

    onSubcardsCreated(createdCards);
  };

  const handleCarryOver = async () => {
    if (statuses.length === 0) return;

    const firstStatus = statuses.reduce<(typeof statuses)[0]>(
      (min, s) => (s.position < min.position ? s : min),
      statuses[0]
    );

    setIsCarryingOver(true);
    try {
      const createResult = await actionOrchestrator.execute<
        CreateTaskInput,
        CreateTaskOutput
      >(
        Action.CREATE_TASK,
        {
          board_id: boardId,
          status_id: firstStatus.id!,
          title: cardTitle,
          priority: (cardPriority ||
            BoardTaskPriority.MEDIUM) as BoardTaskPriority,
        },
        { toast: false }
      );

      if (
        createResult.code !== ResponseCode.SUCCESS ||
        !createResult.data?.task_id
      ) {
        toast.error('Failed to carry over card', {
          message: 'Could not create the new card. Please try again.',
          duration: 3000,
        });
        return;
      }

      const relationResult = await actionOrchestrator.execute<
        CreateTaskRelationInput,
        CreateTaskRelationOutput
      >(
        Action.CREATE_TASK_RELATION,
        {
          source_task_id: taskId,
          target_task_id: createResult.data.task_id,
          relation_type: RELATION_TYPE_CARRY_OVER,
        },
        { toast: false }
      );

      if (relationResult.code !== ResponseCode.SUCCESS) {
        toast.error('Failed to link carried-over card', {
          message: 'The card was created but could not be linked.',
          duration: 3000,
        });
      } else {
        toast.success('Card carried over!', {
          message: 'A new card has been created for this week.',
          duration: 2000,
        });
      }

      // Don't pass cards to onSubcardsCreated — the carried-over card belongs to
      // the current week, not the frozen week being viewed. Passing it would
      // incorrectly add it to the viewed board's cache. The empty array triggers
      // the popup close without patching the board cache.
      onSubcardsCreated([]);
    } catch (error) {
      logger.error('Error carrying over card:', error);
      toast.error('Failed to carry over card', {
        message: 'An unexpected error occurred. Please try again.',
        duration: 3000,
      });
    } finally {
      setIsCarryingOver(false);
    }
  };

  const validHeadingsCount = headings.filter(h => h.trim() !== '').length;
  const hasBlankHeading = headings.some(h => h.trim() === '');

  return (
    <div className="mt-6">
      <label
        className="flex items-center gap-1.5 text-sm font-semibold mb-3"
        style={{ color: colors.text.secondary }}
      >
        <Icon name={IconName.SPARKLES} size="sm" />
        Related Cards
      </label>

      {/* Relations content */}
      {isLoadingRelations ? (
        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border"
          style={{
            backgroundColor: colors.offWhite,
            borderColor: colors.border.default,
            color: colors.text.tertiary,
          }}
        >
          <Icon name={IconName.LOADER} size="sm" className="animate-spin" />
          <span className="text-sm">Loading relations...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Cohort parent — "Cohort task" link */}
          {cohortParent && (
            <div className="space-y-1.5">
              <span
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: colors.text.tertiary }}
              >
                Cohort task
              </span>
              <div
                className="group flex items-center gap-3 px-3 py-2 rounded-lg border transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: withOpacity(colors.blue, opacity.subtle),
                  borderColor: withOpacity(colors.blue, opacity.light),
                }}
              >
                <Icon
                  name={IconName.CHEVRON_UP}
                  size="sm"
                  style={{ color: colors.blue, flexShrink: 0 }}
                />
                <button
                  type="button"
                  onClick={() => onOpenCard?.(cohortParent.source_task.id)}
                  className="flex-1 text-sm font-medium min-w-0 truncate text-left transition-opacity hover:opacity-70"
                  style={{ color: colors.blue }}
                >
                  {cohortParent.source_task.title}
                </button>
                <button
                  type="button"
                  onClick={e =>
                    handleCopyLink(
                      e,
                      DeepLinkEntity.CARD,
                      cohortParent.source_task.id
                    )
                  }
                  aria-label="Copy card link"
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70"
                  style={{ color: colors.blue }}
                >
                  <Icon name={IconName.LINK} size="sm" />
                </button>
              </div>
            </div>
          )}

          {/* Parent cards — "Part of" breadcrumb rows */}
          {parentCards.length > 0 && (
            <div className="space-y-1.5">
              <span
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: colors.text.tertiary }}
              >
                Part of
              </span>
              <div className="space-y-1.5">
                {parentCards.map(rel => (
                  <div
                    key={rel.id}
                    className="group flex items-center gap-3 px-3 py-2 rounded-lg border transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: withOpacity(colors.blue, opacity.subtle),
                      borderColor: withOpacity(colors.blue, opacity.light),
                    }}
                  >
                    <Icon
                      name={IconName.CHEVRON_UP}
                      size="sm"
                      style={{ color: colors.blue, flexShrink: 0 }}
                    />
                    <a
                      href={generateDeepLink(
                        DeepLinkEntity.CARD,
                        rel.source_task.id
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm font-medium min-w-0 truncate transition-opacity hover:opacity-70"
                      style={{ color: colors.blue }}
                    >
                      {rel.source_task.title}
                    </a>
                    <button
                      type="button"
                      onClick={e =>
                        handleCopyLink(
                          e,
                          DeepLinkEntity.CARD,
                          rel.source_task.id
                        )
                      }
                      aria-label="Copy card link"
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70"
                      style={{ color: colors.blue }}
                    >
                      <Icon name={IconName.LINK} size="sm" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Carried over from — source link */}
          {carriedFromSource && (
            <div className="space-y-1.5">
              <span
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: colors.text.tertiary }}
              >
                Carried over from
              </span>
              <div
                className="group flex items-center gap-3 px-3 py-2 rounded-lg border transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: colors.warning.background,
                  borderColor: colors.warning.border,
                }}
              >
                <Icon
                  name={IconName.CORNER_DOWN_RIGHT}
                  size="sm"
                  style={{ color: colors.warning.text, flexShrink: 0 }}
                />
                <a
                  href={generateDeepLink(
                    DeepLinkEntity.CARD,
                    carriedFromSource.source_task.id
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-sm font-medium min-w-0 truncate transition-opacity hover:opacity-70"
                  style={{ color: colors.warning.text }}
                >
                  {carriedFromSource.source_task.title}
                </a>
                <button
                  type="button"
                  onClick={e =>
                    handleCopyLink(
                      e,
                      DeepLinkEntity.CARD,
                      carriedFromSource.source_task.id
                    )
                  }
                  aria-label="Copy card link"
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70"
                  style={{ color: colors.warning.text }}
                >
                  <Icon name={IconName.LINK} size="sm" />
                </button>
              </div>
            </div>
          )}

          {/* Subcards — numbered step rows */}
          {subcards.length > 0 && (
            <div className="space-y-1.5">
              <span
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: colors.text.tertiary }}
              >
                Steps ({subcards.length})
              </span>
              <div className="space-y-1.5">
                {subcards.map((rel, index) => (
                  <div
                    key={rel.id}
                    className="group flex items-center gap-3 px-3 py-2 rounded-lg border transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: withOpacity(
                        colors.primary,
                        opacity.subtle
                      ),
                      borderColor: withOpacity(colors.primary, opacity.light),
                    }}
                  >
                    {/* Numbered bubble */}
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{
                        backgroundColor: withOpacity(
                          colors.primary,
                          opacity.soft
                        ),
                        color: colors.primary,
                      }}
                    >
                      {index + 1}
                    </span>
                    <a
                      href={generateDeepLink(
                        DeepLinkEntity.CARD,
                        rel.target_task.id
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm font-medium min-w-0 truncate transition-opacity hover:opacity-70"
                      style={{ color: colors.text.primary }}
                    >
                      {rel.target_task.title}
                    </a>
                    <button
                      type="button"
                      onClick={e =>
                        handleCopyLink(
                          e,
                          DeepLinkEntity.CARD,
                          rel.target_task.id
                        )
                      }
                      aria-label="Copy card link"
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70"
                      style={{ color: colors.text.tertiary }}
                    >
                      <Icon name={IconName.LINK} size="sm" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Carried over to — target link */}
          {carryOverTarget && (
            <div className="space-y-1.5">
              <span
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: colors.text.tertiary }}
              >
                Carried over to
              </span>
              <div
                className="group flex items-center gap-3 px-3 py-2 rounded-lg border transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: withOpacity(colors.success, opacity.subtle),
                  borderColor: withOpacity(colors.success, opacity.light),
                }}
              >
                <Icon
                  name={IconName.CORNER_DOWN_RIGHT}
                  size="sm"
                  style={{ color: colors.success, flexShrink: 0 }}
                />
                <a
                  href={generateDeepLink(
                    DeepLinkEntity.CARD,
                    carryOverTarget.target_task.id
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-sm font-medium min-w-0 truncate transition-opacity hover:opacity-70"
                  style={{ color: colors.success }}
                >
                  {carryOverTarget.target_task.title}
                </a>
                <button
                  type="button"
                  onClick={e =>
                    handleCopyLink(
                      e,
                      DeepLinkEntity.CARD,
                      carryOverTarget.target_task.id
                    )
                  }
                  aria-label="Copy card link"
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70"
                  style={{ color: colors.success }}
                >
                  <Icon name={IconName.LINK} size="sm" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Carry over button - only for past-week frozen cards */}
      {isFrozenFromPastWeek && !carryOverTarget && !isLoadingRelations && (
        <button
          type="button"
          onClick={handleCarryOver}
          disabled={isCarryingOver}
          className="flex items-center gap-2 mt-4 text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: colors.warning.text }}
        >
          {isCarryingOver ? (
            <>
              <Icon name={IconName.LOADER} size="sm" className="animate-spin" />
              Carrying over...
            </>
          ) : (
            <>
              <Icon name={IconName.CORNER_DOWN_RIGHT} size="sm" />
              Carry over to this week
            </>
          )}
        </button>
      )}

      {/* Breakdown panel toggle - hidden for frozen cards */}
      {!isFrozen && (
        <button
          type="button"
          onClick={() => setIsBreakdownOpen(prev => !prev)}
          className="flex items-center gap-2 mt-4 text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: colors.primary }}
        >
          <Icon name={IconName.SPARKLES} size="sm" />
          Split into steps
          <Icon
            name={isBreakdownOpen ? IconName.CHEVRON_UP : IconName.CHEVRON_DOWN}
            size="sm"
          />
        </button>
      )}

      {/* Breakdown panel */}
      {!isFrozen && isBreakdownOpen && (
        <div
          className="mt-3 p-4 rounded-lg border space-y-3"
          style={{
            backgroundColor: colors.offWhite,
            borderColor: colors.border.default,
          }}
        >
          <p className="text-xs" style={{ color: colors.text.tertiary }}>
            Each step becomes a new card titled "{cardTitle} - [step name]"
          </p>

          {/* Numbered step inputs */}
          <div className="space-y-2">
            {headings.map((heading, index) => (
              <div key={index} className="flex items-center gap-2">
                {/* Step number bubble */}
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: withOpacity(colors.primary, opacity.soft),
                    color: colors.primary,
                  }}
                >
                  {index + 1}
                </span>
                <input
                  type="text"
                  value={heading}
                  onChange={e => handleHeadingChange(index, e.target.value)}
                  onKeyDown={e => handleHeadingKeyDown(e, index)}
                  placeholder={`Step ${index + 1} name`}
                  className="flex-1 px-3 py-2 rounded-lg border text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2"
                  style={{
                    height: sizing.input.md,
                    backgroundColor: colors.background,
                    borderColor: colors.border.default,
                    color: colors.text.primary,
                    ['--tw-ring-color' as string]: withOpacity(
                      colors.primary,
                      opacity.medium
                    ),
                  }}
                  disabled={isCreatingSubcards}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveHeading(index)}
                  disabled={headings.length <= 1 || isCreatingSubcards}
                  className="flex-shrink-0 p-1.5 rounded transition-opacity hover:opacity-70"
                  style={{
                    color: colors.text.tertiary,
                    opacity: headings.length <= 1 ? 0.3 : 1,
                  }}
                  aria-label="Remove step"
                >
                  <Icon name={IconName.X} size="sm" />
                </button>
              </div>
            ))}
          </div>

          {/* Add another step */}
          <button
            type="button"
            onClick={handleAddHeading}
            disabled={isCreatingSubcards}
            className="flex items-center gap-1.5 text-sm transition-opacity hover:opacity-70"
            style={{ color: colors.primary }}
          >
            <Icon name={IconName.PLUS} size="sm" />
            Add another step
          </button>

          {/* Error message */}
          {creationError && (
            <p className="text-xs" style={{ color: colors.error }}>
              {creationError}
            </p>
          )}

          {/* Create button */}
          <button
            type="button"
            onClick={handleCreateSubcards}
            disabled={
              isCreatingSubcards || validHeadingsCount === 0 || hasBlankHeading
            }
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
            style={{
              height: sizing.input.md,
              backgroundColor:
                isCreatingSubcards ||
                validHeadingsCount === 0 ||
                hasBlankHeading
                  ? withOpacity(colors.primary, opacity.medium)
                  : colors.primary,
              color: colors.text.inverse,
              cursor:
                isCreatingSubcards ||
                validHeadingsCount === 0 ||
                hasBlankHeading
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            {isCreatingSubcards ? (
              <>
                <Icon
                  name={IconName.LOADER}
                  size="sm"
                  className="animate-spin"
                />
                Creating steps...
              </>
            ) : (
              `Create ${validHeadingsCount > 0 ? validHeadingsCount : ''} Step${validHeadingsCount !== 1 ? 's' : ''}!`
            )}
          </button>
        </div>
      )}
    </div>
  );
}
